import { provideAppInitializer, inject, type ApplicationConfig } from '@angular/core';
import type { MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors, withFetch, withXsrfConfiguration } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { authInterceptor, refreshTokenInterceptor, csrfTokenInterceptor, ConfigService } from '@sortaify/client-core';
import { sentryInterceptor } from './core/interceptors/sentry.interceptor';
import * as Sentry from '@sentry/angular';

export const tooltipConfig: MatTooltipDefaultOptions = {
  showDelay: 200,
  hideDelay: 0,
  touchendHideDelay: 500,
  tooltipClass: 'sortaify-tooltip',
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    // Load config.json at app startup. Services access values via ConfigService.
    provideAppInitializer(() => {
      const configService = inject(ConfigService);
      // Initialize Sentry after config loads
      const initSentry = () => {
        const dsn = configService.sentryDsn();
        if (dsn) {
          Sentry.init({
            dsn,
            release: `web@${configService.version()}`,
            environment: 'web',
            sendDefaultPii: false,
            defaultIntegrations: false,
          });
          // Set up global error handlers
          window.addEventListener('unhandledrejection', (event) => {
            Sentry.captureException(event.reason, {
              contexts: { runtime: { type: 'unhandledrejection' } },
            });
          });
          window.addEventListener('error', (event) => {
            Sentry.captureException(event.error, {
              contexts: { runtime: { type: 'window_error' } },
            });
          });
        }
      };
      return configService.load().then(() => {
        initSentry();
      });
    }),
    provideHttpClient(
      withInterceptors([authInterceptor, refreshTokenInterceptor, sentryInterceptor, csrfTokenInterceptor]),
      withFetch(),
      // Angular reads sa_x cookie automatically and adds X-XSRF-TOKEN header
      // on POST/PUT/PATCH/DELETE. Requires relative URLs (no http:// prefix).
      withXsrfConfiguration({ cookieName: 'sa_x', headerName: 'X-XSRF-TOKEN' }),
    ),
    provideAnimationsAsync(),
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipConfig },
  ],
};
